import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, Circle, ArrowRight, BookOpen, User, Upload, Star, Loader2, Settings, Camera, Users, TrendingUp, Sparkles } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useOnboardingStatus } from '@/hooks/useOnboardingStatus';
import { cn } from '@/lib/utils';
import { getOnboardingNextUrl } from '@/lib/onboarding';

const REQUIRED_GENRES_MIN = 3;

export default function Onboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const onboardingStatus = useOnboardingStatus();

  // Check if in retake mode
  const isRetakeMode = sessionStorage.getItem('retakeOnboarding') === '1' || 
                       searchParams.get('retake') === '1';

  // Auth guard
  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
  }, [user, navigate]);

  // Auto-redirect to next step if not already complete (skip if retaking)
  useEffect(() => {
    if (isRetakeMode) return; // Skip auto-redirect in retake mode
    
    if (user && !onboardingStatus.loading && onboardingStatus.nextStep !== 'done') {
      const currentPath = window.location.pathname;
      
      // Don't redirect if already on the correct step
      if (
        (onboardingStatus.nextStep === 'profile' && currentPath === '/onboarding/profile') ||
        (onboardingStatus.nextStep === 'import' && currentPath === '/onboarding/import') ||
        (onboardingStatus.nextStep === 'bookshelf-scan' && currentPath === '/onboarding/bookshelf-scan') ||
        (onboardingStatus.nextStep === 'genres' && currentPath === '/onboarding/genres')
      ) {
        return;
      }

      // Auto-redirect to next step
      if (onboardingStatus.nextStep === 'profile') {
        navigate('/onboarding/profile', { replace: true });
      } else if (onboardingStatus.nextStep === 'import') {
        navigate('/onboarding/import', { replace: true });
      } else if (onboardingStatus.nextStep === 'bookshelf-scan') {
        navigate('/onboarding/bookshelf-scan', { replace: true });
      } else if (onboardingStatus.nextStep === 'genres') {
        navigate('/onboarding/genres', { replace: true });
      }
    }
  }, [user, onboardingStatus, navigate, isRetakeMode]);

  if (!user || onboardingStatus.loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const steps = [
    {
      id: 'profile',
      title: 'Complete your profile',
      description: 'Set your username and choose an avatar',
      icon: User,
      completed: onboardingStatus.hasProfile,
      required: true,
      path: '/onboarding/profile',
    },
    {
      id: 'import',
      title: 'Import past reading data',
      description: 'Upload your Goodreads CSV (optional)',
      icon: Upload,
      completed: onboardingStatus.importComplete,
      required: false,
      path: '/onboarding/import',
    },
    {
      id: 'bookshelf-scan',
      title: 'Scan your bookshelf',
      description: 'Take up to 5 photos to quickly add books',
      icon: Camera,
      completed: onboardingStatus.bookshelfScanComplete,
      required: false,
      path: '/onboarding/bookshelf-scan',
    },
    {
      id: 'genres',
      title: 'Select your genres',
      description: `Pick at least ${REQUIRED_GENRES_MIN} genres and rate some books`,
      icon: Star,
      completed: onboardingStatus.genresComplete,
      required: true,
      path: '/onboarding/genres',
    },
  ];

  const completedSteps = steps.filter(step => step.completed).length;
  const totalSteps = steps.length;
  const progressPercentage = (completedSteps / totalSteps) * 100;

  const handleResumeOnboarding = () => {
    const nextUrl = getOnboardingNextUrl(onboardingStatus);
    navigate(nextUrl);
  };

  const handleStepClick = (path: string) => {
    if (isRetakeMode) {
      navigate(path);
    }
  };

  const handleDoneEditing = () => {
    sessionStorage.removeItem('retakeOnboarding');
    navigate('/');
  };

  const isComplete = onboardingStatus.nextStep === 'done';

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-full mx-auto flex items-center justify-center mb-4">
            <BookOpen className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-3xl font-heading font-bold mb-2">
            {isComplete && isRetakeMode ? 'Edit Your Setup' : isComplete ? 'Welcome to Dino!' : 'Complete Your Setup'}
          </h1>
          <p className="text-muted-foreground mb-4">
            {isComplete && isRetakeMode
              ? 'Update your profile, import data, or adjust your preferences.'
              : isComplete 
                ? 'Your profile is all set up! Start exploring your personalized reading experience.'
                : 'Complete these steps to unlock the full Dino experience.'
            }
          </p>
          {!isRetakeMode && !isComplete && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6">
              <div className="flex flex-col items-center gap-2 p-4 rounded-lg border bg-card">
                <Users className="w-5 h-5 text-primary" />
                <p className="text-xs font-medium text-center">Turn your reading journey into a shared adventure</p>
              </div>
              <div className="flex flex-col items-center gap-2 p-4 rounded-lg border bg-card">
                <TrendingUp className="w-5 h-5 text-secondary" />
                <p className="text-xs font-medium text-center">Watch your reading habit grow</p>
              </div>
              <div className="flex flex-col items-center gap-2 p-4 rounded-lg border bg-card">
                <Sparkles className="w-5 h-5 text-accent" />
                <p className="text-xs font-medium text-center">Find your next favourite read, tailored to you</p>
              </div>
            </div>
          )}
        </div>

        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl">Setup Progress</CardTitle>
              <Badge variant={isComplete ? "default" : "secondary"}>
                {completedSteps} of {totalSteps} complete
              </Badge>
            </div>
            <Progress value={progressPercentage} className="mt-4" />
          </CardHeader>
          <CardContent className="space-y-4">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isCurrentStep = onboardingStatus.nextStep === step.id;
              const isClickable = isRetakeMode;
              
              return (
                <div
                  key={step.id}
                  onClick={() => isClickable && handleStepClick(step.path)}
                  className={cn(
                    "flex items-center gap-4 p-4 rounded-lg border transition-colors",
                    isClickable && "cursor-pointer hover:bg-muted/50",
                    step.completed 
                      ? "border-success/20 bg-success/5" 
                      : isCurrentStep 
                        ? "border-primary/20 bg-primary/5" 
                        : "border-border"
                  )}
                >
                  <div className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center",
                    step.completed 
                      ? "bg-success text-success-foreground" 
                      : isCurrentStep 
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                  )}>
                    {step.completed ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <StepIcon className="w-5 h-5" />
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{step.title}</h3>
                      {step.required && !step.completed && (
                        <Badge variant="outline" className="text-xs">Required</Badge>
                      )}
                      {!step.required && (
                        <Badge variant="secondary" className="text-xs">Optional</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  </div>
                  
                  {isCurrentStep && !step.completed && (
                    <ArrowRight className="w-5 h-5 text-primary" />
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        <div className="flex flex-col gap-4 items-center">
          {isComplete && isRetakeMode ? (
            <>
              <Button onClick={() => navigate('/onboarding/archetype?retake=1', { replace: true, state: { retake: true } })} size="lg" className="w-full max-w-sm">
                <Settings className="w-5 h-5 mr-2" />
                Retake Preferences Quiz
              </Button>
              <Button onClick={handleDoneEditing} variant="outline" size="lg" className="w-full max-w-sm">
                Done Editing
              </Button>
            </>
          ) : isComplete ? (
            <Button onClick={() => navigate('/')} size="lg">
              <BookOpen className="w-5 h-5 mr-2" />
              Start Reading
            </Button>
          ) : (
            <div className="flex gap-4">
              <Button 
                variant="outline" 
                onClick={() => navigate('/')}
              >
                Skip for Now
              </Button>
              <Button onClick={handleResumeOnboarding} size="lg">
                {onboardingStatus.nextStep === 'profile' ? 'Get Started' : 'Continue Setup'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}